import { useCallback, useEffect, useState } from 'react';
import {
  sreApi,
  type ActivityEntry,
  type BackupRecord,
  type CapacityForecast,
  type ComponentHealth,
  type DeploymentRecord,
  type IncidentRecord,
  type RecoveryRecord,
  type Runbook,
  type SLODefinition,
  type SLIDefinition,
  type SREDashboard,
} from '../../api/sre';

type Tab = 'overview' | 'infrastructure' | 'deployments' | 'backup' | 'recovery' | 'incidents' | 'capacity' | 'health';

function stateColor(state: string) {
  if (state === 'healthy') return 'text-emerald-700 bg-emerald-50';
  if (state === 'degraded') return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
}

function severityColor(sev: string) {
  if (sev === 'sev1') return 'text-red-700 bg-red-50';
  if (sev === 'sev2') return 'text-orange-700 bg-orange-50';
  if (sev === 'sev3') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

export function SREPlatformPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SREDashboard | null>(null);
  const [components, setComponents] = useState<ComponentHealth[]>([]);
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([]);
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [recoveries, setRecoveries] = useState<RecoveryRecord[]>([]);
  const [incidents, setIncidents] = useState<IncidentRecord[]>([]);
  const [runbooks, setRunbooks] = useState<Runbook[]>([]);
  const [slos, setSlos] = useState<SLODefinition[]>([]);
  const [slis, setSlis] = useState<SLIDefinition[]>([]);
  const [capacity, setCapacity] = useState<CapacityForecast | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, health, deps, bkp, rec, inc, rb, slo, sli, cap, act] = await Promise.all([
        sreApi.dashboard(),
        sreApi.listHealth(),
        sreApi.listDeployments(),
        sreApi.listBackups(),
        sreApi.listRecoveries(),
        sreApi.listIncidents(),
        sreApi.listRunbooks(),
        sreApi.listSLOs(),
        sreApi.listSLIs(),
        sreApi.capacityForecast(),
        sreApi.listActivity(),
      ]);
      setDashboard(dash);
      setComponents(health);
      setDeployments(deps);
      setBackups(bkp);
      setRecoveries(rec);
      setIncidents(inc);
      setRunbooks(rb);
      setSlos(slo);
      setSlis(sli);
      setCapacity(cap);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load SRE platform');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const triggerBackup = async () => {
    setBusy(true);
    try {
      await sreApi.triggerBackup({ type: 'database' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const createDeployment = async () => {
    setBusy(true);
    try {
      await sreApi.createDeployment({ service: 'platform-api', version: 'v2.15.0', strategy: 'canary' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'infrastructure', label: 'Infrastructure' },
    { id: 'deployments', label: 'Deployments' },
    { id: 'backup', label: 'Backup Center' },
    { id: 'recovery', label: 'Recovery Center' },
    { id: 'incidents', label: 'Incidents' },
    { id: 'capacity', label: 'Capacity' },
    { id: 'health', label: 'Health Explorer' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Platform Operations &amp; SRE</h1>
          <p className="text-sm text-slate-500">
            High availability, disaster recovery, deployments &amp; observability — FCP-00015
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Availability', value: `${dashboard.availability_pct.toFixed(2)}%` },
              { label: 'Active Incidents', value: dashboard.active_incidents },
              { label: 'Open Deployments', value: dashboard.open_deployments },
              { label: 'SLOs Met', value: `${dashboard.slos_met}/${dashboard.slos_total}` },
              { label: 'Backups (24h)', value: dashboard.backups_24h },
              { label: 'Recoveries Validated', value: dashboard.recoveries_validated },
              { label: 'Components Healthy', value: `${dashboard.components_healthy}/${dashboard.components_total}` },
              { label: 'Error Budget Remaining', value: `${dashboard.error_budget_remaining_pct.toFixed(1)}%` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          {activity.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">Recent Activity</h2>
              <ul className="mt-3 space-y-2">
                {activity.slice(0, 8).map((a) => (
                  <li key={a.id} className="flex justify-between text-sm text-slate-600">
                    <span>{a.message}</span>
                    <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {tab === 'infrastructure' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">SLOs</h2>
            <ul className="mt-3 space-y-2">
              {slos.map((s) => (
                <li key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <span>{s.name} ({s.service})</span>
                  <span className="font-medium">{s.target}% · budget {s.budget_remaining.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Runbooks</h2>
            <ul className="mt-3 space-y-2">
              {runbooks.map((rb) => (
                <li key={rb.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <p className="font-medium">{rb.title}</p>
                  <p className="text-xs text-slate-500">{rb.category} · {rb.automated ? 'Automated' : 'Manual'}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'deployments' && (
        <div className="space-y-4">
          <button type="button" onClick={createDeployment} disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            Start Canary Deployment
          </button>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Strategy</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Region</th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-medium">{d.service}</td>
                    <td className="px-4 py-3">{d.version}</td>
                    <td className="px-4 py-3">{d.strategy}</td>
                    <td className="px-4 py-3">{d.status}</td>
                    <td className="px-4 py-3">{d.region ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'backup' && (
        <div className="space-y-4">
          <button type="button" onClick={triggerBackup} disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            Trigger Database Backup
          </button>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Verified</th>
                  <th className="px-4 py-3">Started</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100">
                    <td className="px-4 py-3">{b.type}</td>
                    <td className="px-4 py-3">{b.status}</td>
                    <td className="px-4 py-3">{(b.size_bytes / (1024 * 1024)).toFixed(0)} MB</td>
                    <td className="px-4 py-3">{b.verified ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3">{new Date(b.started_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'recovery' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">RPO</th>
                <th className="px-4 py-3">RTO</th>
                <th className="px-4 py-3">Validated</th>
              </tr>
            </thead>
            <tbody>
              {recoveries.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No recovery operations yet</td></tr>
              ) : recoveries.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="px-4 py-3">{r.type}</td>
                  <td className="px-4 py-3">{r.status}</td>
                  <td className="px-4 py-3">{r.rpo_seconds ? `${r.rpo_seconds}s` : '—'}</td>
                  <td className="px-4 py-3">{r.rto_seconds ? `${r.rto_seconds}s` : '—'}</td>
                  <td className="px-4 py-3">{r.validated ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'incidents' && (
        <div className="space-y-3">
          {incidents.map((inc) => (
            <div key={inc.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium uppercase ${severityColor(inc.severity)}`}>
                  {inc.severity}
                </span>
                <span className="text-sm font-medium text-slate-900">{inc.title}</span>
                <span className="text-xs text-slate-500">{inc.status}</span>
              </div>
              {inc.service && <p className="mt-1 text-xs text-slate-500">Service: {inc.service}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'capacity' && capacity && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Storage (GB)', value: capacity.storage_gb, growth: capacity.storage_growth_pct },
            { label: 'API Calls / Hour', value: capacity.api_calls_hour.toLocaleString(), growth: capacity.api_growth_pct },
            { label: 'AI Usage / Hour', value: capacity.ai_usage_hour.toLocaleString(), growth: capacity.ai_growth_pct },
            { label: 'Database Size (GB)', value: capacity.db_size_gb, growth: capacity.storage_growth_pct },
            { label: 'Cost Growth', value: `${capacity.cost_growth_pct}%`, growth: capacity.cost_growth_pct },
          ].map((item) => (
            <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{item.value}</p>
              <p className="mt-1 text-xs text-emerald-600">+{item.growth}% projected</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {components.map((c) => (
            <div key={c.component} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="font-medium text-slate-900">{c.component}</p>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${stateColor(c.state)}`}>{c.state}</span>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-1 text-xs text-slate-500">
                {c.latency_ms != null && <><dt>Latency</dt><dd>{c.latency_ms} ms</dd></>}
                {c.error_rate != null && <><dt>Error Rate</dt><dd>{c.error_rate}%</dd></>}
                {c.replicas != null && <><dt>Replicas</dt><dd>{c.replicas}</dd></>}
              </dl>
            </div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:col-span-2 lg:col-span-3">
            <h2 className="text-sm font-semibold text-slate-900">Service Level Indicators</h2>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {slis.map((s) => (
                <li key={s.id} className="flex justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <span>{s.name}</span>
                  <span>{s.current} / {s.target} {s.unit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
